// Providers
export { CofheProvider, useCofheContext } from './providers/index';

// Hooks
export {
  ETH_ADDRESS_LOWERCASE,
  getCofheTokenClaimUnshieldedCallArgs,
  getCofheTokenShieldCallArgs,
  getCofheTokenUnshieldCallArgs,
  useCofheActiveACP,
  useCofheAllACPs,
  useCofheClient,
  useCofheConnection,
  useCofheEnabled,
  useCofheEncrypt,
  useCofhePublicClient,
  useCofheRemoveACP,
  useCofheSelectACP,
  useCofheSimulateWriteContract,
  useCofheStatuses,
  useCofheTokenClaimable,
  useCofheTokenClaims,
  useCofheTokenApprove,
  useCofheTokenClaimUnshielded,
  useCofheTokenDecryptedBalance,
  useCofheTokenPublicBalance,
  useCofheTokenLists,
  useCofheToken,
  useKnownCofheToken,
  useCofheTokens,
  useCofheTokensClaimable,
  useCofheTokenShield,
  useCofheTokensWithExistingEncryptedBalances,
  useCofheTokenTransactions,
  useCofheTokenTransfer,
  useResolvedCofheToken,
  useCofheTransactions,
  addCofheTransaction,
  useCofheTokenUnshield,
  useCofheWalletClient,
  useCofheWriteContract,
  useCoingeckoContractMarketChartRange,
  useCoingeckoUsdPrice,
  useTokenAllowance,
  useTokensWithPublicBalances,
  useTransactionReceiptsByHash,
  type ClaimableAmountByTokenAddress,
  type CoingeckoMarketChartPoint,
  type Erc20Pair,
  type UnshieldClaim,
  type UnshieldClaimsSummary,
  type UnshieldClaimsSummaryByTokenAddress,
  type UseCoingeckoContractMarketChartRangeInput,
  type UseCoingeckoContractMarketChartRangeOptions,
  type UseCoingeckoUsdPriceInput,
  type UseCoingeckoUsdPriceOptions,
  type UseCofheTokenTransactionsInput,
  type UseCofheTokenTransactionsResult,
  type UseTransactionReceiptsByHashInput,
  type AddCofheTransactionInput,
} from '@/hooks/index';

export { useCofheEncryptAndWriteContract } from '@/hooks/useCofheEncryptAndWriteContract';
export { useCofheReadContractAndDecrypt } from '@/hooks/useCofheReadContractAndDecrypt';

// Utils
export {
  FheTypesList,
  formatRelativeTime,
  getBlockExplorerAddressUrl,
  getBlockExplorerTokenUrl,
  getBlockExplorerTxUrl,
  getBlockExplorerUrl,
  isEthPair,
  isWrappedEthToken,
  truncateHash,
} from './utils/index';

// Stores
export { useCustomTokensStore } from './stores/customTokensStore';

export {
  actionToString,
  isCustomTransactionActionType,
  statusToString,
  transactionMatchesToken,
  TransactionActionType,
  TransactionStatus,
  useTransactionStore,
  type Transaction,
  type BuiltInTransactionActionType,
  type CustomTransactionActionType,
  type JsonValue,
  type NewTransaction,
  type TransactionActionString,
  type TransactionStatusString,
  type TransactionStore,
} from './stores/transactionStore';

export { createCofheConfig } from './config';
export type { CofheReactLogger, CofheReactLoggerMethod } from './config';

// Types
export type {
  CofheClientConfig,
  CofheContextValue,
  CofheProviderProps,
  TransactionRenderer,
  TransactionRendererProps,
  TransactionRenderers,
} from './types/index';

export { createCofheClient } from '@cofhe/sdk/web';

export { useCofheCreateACP } from '@/hooks/acps/useCofheCreateACP';
export { useCofheNavigateToCreateACP } from '@/hooks/acps/useCofheNavigateToCreateACP';
export { useCofheAutoConnect } from '@/hooks/useCofheAutoConnect';
export { CofheFloatingButtonProvider, sortCofheStatuses } from './components/index';
export { COFHE_STATUS_IDS, CofheStatusActionIntents } from './components/CofheFloatingButton/types';

export type { CofheConfigWithReact as CofhesdkConfigWithReact } from './config';

export type { CofheStatus, CofheStatusActionIntent, CofheStatusId } from './components/CofheFloatingButton/types';
export type { ConfidentialToken } from './types/token';
export { getConfidentialDecimals, getPublicDecimals, getPublicSymbol } from './types/token';
export { scaleAmount, quantizeAmount } from './utils/format';

export type { CofheDecryptMeta, CofheQueryMeta } from './meta';

export { useCofheDecryptionActivity } from '@/hooks/useCofheDecryptionActivity';
export type {
  DecryptionActivityRow,
  DecryptionActivityResult,
  DecryptionActivityCategory,
  UseCofheDecryptionActivityOptions,
  FetchStageState,
  DecryptStageState,
  ACPState,
} from '@/hooks/useCofheDecryptionActivity';

export { useInternalQueryClient } from './providers/index';

// Type bridges for cross-repo / multi-instance viem peer dependency scenarios
export {
  asCofhePublicClient,
  tryAsCofhePublicClient,
  asCofheWalletClient,
  tryAsCofheWalletClient,
} from './utils/viemClientBridge';
export type {
  PublicClientLike as CofhePublicClientLike,
  WalletClientLike as CofheWalletClientLike,
} from './utils/viemClientBridge';

// Block-aware cache invalidation primitives.
//
// These are the building blocks behind the SDK's own "wait until the serving RPC
// node has the mined block before returning a read" behaviour. They are exported
// so consumers (e.g. a wagmi-based app) can build the same reliability into their
// own reads + post-tx invalidation, sharing THIS module's single invalidation-
// context store — so an invalidation registered here is honoured by both the
// SDK's confidential reads and the consumer's own block-aware queries.
export {
  invalidateQueriesWithContext,
  withInvalidationContext,
  findMatchingInvalidationContext,
  consumeInvalidationContext,
  type InvalidationContextQueryFilters,
  type QueryFunctionWithInvalidationContext,
} from './utils/invalidationContext';
export {
  maybeWaitUntilRpcAware,
  maybeWaitUntilRpcAwareAndReadContract,
  type WaitUntilRpcAwareAndReadContractOptions,
  type ReadContractResult,
} from './utils/waitUntilRpcAwareAndReadContract';
export { resolveReceiptBlockHash } from './utils/resolveReceiptBlockHash';
export { useInvalidationContextStore, type InvalidationContextEntry } from './stores/invalidationContextStore';
